import { FC, useState } from "react";
import {
    IonAlert,
    IonCard,
    IonItem,
    IonText,
    useIonRouter,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { routes } from "../route/constants";
import AppleImage from "../assets/images/watches/watchOS.png";
import WearImage from "../assets/images/watches/wearOS.png";
import GarminImage from "../assets/images/watches/garmin.png";
import SamsungImage from "../assets/images/watches/tizen.png";
import "./SelectWatchComponent.css";
import { trackEvent } from "../services/analytics/analyticsService";
import { useUsername } from "./hooks";
import { useDispatch } from "react-redux";
import { setShowOnBoardingFlow } from "../redux/actions/appStatus";
import { GarminSDK } from "capacitor-garmin-sdk";
import { isIos } from "../services/platformDetection";
import { openStoreForConnectMobile } from "../services/watch/GarminWatchHelper";

export const SelectWatchComponent: FC = () => {
    const { t } = useTranslation();
    const route = useIonRouter();
    const MP_EVENT = "PairingSelectWatch";
    const username = useUsername();
    const dispatch = useDispatch();

    const [showInstallConnectAlert, setShowInstallConnectAlert] =
        useState(false);

    const handleGarminWatch = async () => {
        // check if connect app installed
        const { installed } = await GarminSDK.checkGarminConnectInstalled();
        if (installed) {
            // check if the version on the watch is compatible with bluetooth syncing
            route.push(routes.onboardingSelectGarmin);
        } else {
            // open the alert to download the app
            setShowInstallConnectAlert(true);
        }
    };

    const onWatchSelected = (platform: string) => {
        trackEvent(MP_EVENT, {
            watchPlatform: platform,
            hasWatch: true,
        });

        if (platform === "garmin") {
            handleGarminWatch().catch((e) => {
                console.error("Error handling Garmin watch", e);
            });
        } else {
            route.push(routes.onboardingPairWatch + `/${platform}`);
        }
    };

    const renderWatchButton = (
        image: any,
        alt: string,
        platform: string,
        subtitle?: string
    ) => {
        return (
            <IonItem
                data-testid={`pair-watch-list-${platform}`}
                className="item-pair-watch-list"
                lines="none"
                onClick={onWatchSelected.bind(null, platform)}
                button
            >
                <img
                    src={image}
                    height={20}
                    alt={alt}
                    style={{ margin: "20px 0" }}
                />
                {subtitle && <IonText>{subtitle}</IonText>}
            </IonItem>
        );
    };

    const renderWatchList = () => {
        return (
            <>
                <IonCard
                    color="transparent"
                    data-testid="pair-watch-select-device"
                >
                    {renderWatchButton(GarminImage, "Garmin", "garmin")}
                </IonCard>
                <IonCard color="transparent">
                    <IonItem
                        data-testid="no-watch-item"
                        className="item-pair-watch-list"
                        button
                        lines="none"
                        onClick={() => {
                            trackEvent(MP_EVENT, {
                                watchPlatform: "none",
                                hasWatch: false,
                            });
                            dispatch(setShowOnBoardingFlow(false));
                            return route.push(
                                routes.onBoardingSetupComplete,
                                "root",
                                "replace"
                            );
                        }}
                    >
                        <IonText className="text-large">
                            {t("help.onBoarding.noWatch")}
                        </IonText>
                    </IonItem>
                </IonCard>
                <IonAlert
                    isOpen={showInstallConnectAlert}
                    header={t("general.installApp", {
                        appName: "Garmin Connect",
                    })}
                    message={t("garminWatches.connectNotInstalled")}
                    buttons={[
                        {
                            text: t("general.cancel"),
                            role: "cancel",
                            handler: () => {
                                setShowInstallConnectAlert(false);
                            },
                        },
                        {
                            text: t("general.installApp", {
                                appName: "Garmin Connect",
                            }),
                            role: "confirm",
                            handler: () => {
                                setShowInstallConnectAlert(false);
                                openStoreForConnectMobile();
                            },
                        },
                    ]}
                    onDidDismiss={({ detail }) =>
                        console.log(`Dismissed with role: ${detail.role}`)
                    }
                ></IonAlert>
            </>
        );
    };

    return <>{renderWatchList()}</>;
};
